import styled from "styled-components";

// ===========================
// Component Props Interfaces
// ===========================
interface CalendarLengthButtonProps {
  selected?: boolean;
}

// ===========================
// Button Components
// ===========================
export const CalendarLengthButton = styled.button<CalendarLengthButtonProps>`
  /* Base styles */
  background-color: ${({ selected }) => (selected ? "#ff6a0c" : "#cccccc")};
  color: ${({ selected }) => (selected ? "white" : "#666")};
  border: none;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: none;

  /* Typography */
  font-size: 12px;
  line-height: 12px;
  font-weight: bold;

  /* Sizing */
  padding: 1rem 4rem;
  margin-right: 1rem;
  min-width: unset;
  width: auto;
  height: 40px;

  /* States */
  &:hover {
    box-shadow: none;
  }

  &.active {
    background-color: #cc4e00;
  }

  /* Mobile styles */
  @media (max-width: 768px) {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    margin-right: 0;
    padding: 1rem 0;
    font-size: 11px;
    line-height: 11px;
    height: 30px;
  }
`;

export const CalendarLengthButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 0;

  @media (max-width: 768px) {
    gap: 0.5rem;
    height: 28px;

    .calendar-length-button {
      min-width: 0;
      width: 100%;
      margin-right: 0;
      flex: 1 1 0;
    }
  }
`;

// ===========================
// Calendar Component
// ===========================
export const StyledAppCalendar = styled.div<{ $span?: string }>`
  /* Base styles */
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: flex;
  justify-content: center;

  /* ===========================
     Calendar Base Structure
  =========================== */
  .rbc-calendar {
    width: 100%;
    max-width: 100%;
  }

  /* ===========================
     View-specific Styles
  =========================== */

  /* Month View */
  .rbc-month-view {
    max-width: 100%;
    min-width: unset;
  }

  .rbc-month-view-short {
    height: unset;
  }

  .rbc-month-row {
    flex: 1 1 0%;
    min-height: fit-content !important;
    height: fit-content !important;
    overflow: unset !important;
  }

  /* Time View */
  .rbc-time-view {
    min-width: ${({ $span }) => $span === 'day' ? 'fit-content' : '100%'};
    min-height: unset !important;
  }

  .rbc-time-content {
    overflow: unset !important;
  }

  /* ===========================
     Headers
  =========================== */
  .rbc-header {
    background-color: #e4e4e4 !important;
    font-weight: normal;
    text-align: center;
    padding: 0;
    display: flex;
    height: 25px;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    border-right: 1px solid white;
    border-left: 1px solid #bbb;
  }

  .rbc-time-header-cell {
    min-height: 25px;
    background-color: #e4e4e4 !important;
    display: ${({ $span }) => ($span === 'day' || $span === 'week') ? 'flex' : 'none'};
    position: sticky;
    top: 0;
    z-index: 12;
  }

  .rbc-time-header {
    overflow: ${({ $span }) => ($span === 'day') ? 'unset' : 'hidden'};
  }

  /* Sticky Gutter */
  .rbc-time-gutter.rbc-time-column {
    position: sticky !important;
    left: 0;
    background-color: #e4e4e4;
    z-index: 10;
    border-right: 1px solid #bbb;
    min-height: 100% !important;
    max-height: 100% !important;
  }

  .rbc-label.rbc-time-header-gutter {
    position: sticky;
    top: 0;
    left: 0;
    background: #e4e4e4;
    border-right: 1px solid #bbb;
  }

  .rbc-month-view .rbc-row.rbc-month-header {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  /* ===========================
     Day/Cell Styles
  =========================== */
  .rbc-day-slot {
    max-width: 100%;
  }

  .rbc-row-content {
    height: 100%;
    max-height: none;
  }

  .rbc-row-bg {
    height: 100%;
  }

  .rbc-off-range-bg {
    background: transparent;
  }

  .rbc-button-link {
    width: 100%;
    pointer-events: none !important;
  }

  .rbc-row-segment {
    padding: 1px 1px;
  }

  .date-is-today {
    background: red;
    color: white;
    font-size: 12px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
  }

  .date-is-today-header {
    color: red;
  }

  /* ===========================
     Event Styles
  =========================== */
  .rbc-event {
    transition: transform 0.2s ease-out, box-shadow 0.2s ease-out !important;

    &:hover {
      transform: translateY(-3px) !important;
      box-shadow: 0px 3px 8px rgba(0, 0, 0, 0.2) !important;
    }
  }

  .rbc-addons-dnd-dragged-event {
    transform: none !important;
    box-shadow: none !important;
    opacity: 0.5;
  }

  .rbc-event-content {
    height: 100%;

    > div {
      border-radius: 0 !important;
    }
  }

  .rbc-event-label {
    display: none;
  }

  /* ===========================
     Agenda View
  =========================== */
  .rbc-agenda-table {
    border: none !important;

    thead {
      display: none;
    }
  }

  .rbc-agenda-time-cell {
    display: none;
  }

  .rbc-agenda-content {
    background: white;
    border-radius: 6px;
    margin: 1rem;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

    .rbc-agenda-table tr {
      background: none !important;
      border-radius: unset !important;
    }
  }

  .rbc-agenda-view table.rbc-agenda-table tbody > tr > td + td {
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
    padding-left: 1px !important;
  }

  /* Separator lines between days in agenda view */
  .rbc-agenda-date-cell {
    border-bottom: 1px solid #ddd !important;
  }

  .rbc-agenda-event-cell {
    border-bottom: 1px solid #ddd !important;
  }

  /* ===========================
     Utility Classes
  =========================== */
  .rbc-current-time-indicator {
    height: 1px;
    background-color: unset;
    border-bottom: 2px solid red;
    z-index: 9;
  }

  .rbc-overflowing {
    margin-right: 0 !important;
    overflow: hidden !important;
  }

  .rbc-show-more {
    font-size: 12px;
    padding-left: 5px;
    margin-top: 2px;
  }

  /* Fade animation */
  .fade-in {
    opacity: 0;
    transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &.visible {
      opacity: 1;
    }
  }

  .rbc-slot-selection {
    background-color: rgba(0, 0, 0, 0.05) !important;
    color: red !important;
    font-size: 14px !important;
    font-weight: bold !important;
    border: 1px dashed rgba(0, 0, 0, 0.5) !important;
  }

  /* ===========================
     Additional UI Elements
  =========================== */
  .MuiButton-root {
    width: 100%;
  }

  .label-date {
    display: none;
  }

  .back-next-buttons {
    display: flex;
    justify-content: space-between;

    .btn-current {
      display: flex;
      align-items: center;
    }
  }
`;

export const StyledDivider = styled.div`
  height: 1rem;
  display:block;
`;

// ===========================
// Title Section
// ===========================
export const InstrumentTitleSection = styled.div`
  /* Container styles */
  border-radius:6px;
  background-color: #e4e4e4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  margin-bottom: 1rem;
  width:100%;

  /* Main title styles */
  .main {
    font-weight: bold;
    font-size: 2rem;
    display: block;
    display: -webkit-box;
    line-height: 1.2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    max-width: 100%;
    margin: 0;
    color: #000;
  }

  /* Subtitle styles */
  .subTitle {
    padding: 0.8rem;
    margin: 6px;
    border-radius: 6px;
    background-color: #ffffff;
    color: #000;
    font-size: 1.2rem;
    display: inline-block;
    font-weight: normal;
    margin-right:-0.5rem;
  }

  /* Mobile styles */
  @media (max-width: 768px) {
    .main {
      font-size: 1.4rem;
    }

    .subTitle {
      font-size: 1.2rem;
    }
  }
`;
